/**
 * Canonical Project Type taxonomy.
 * Single source of truth for every form, the Work-page filter labels,
 * and the homepage "What we do" list.
 *
 * `value` is the string persisted / sent to Zoho and used as the option key.
 * `label` is the display label. `description` is shown by the Labs radio cards.
 */

export interface ProjectTypeOption {
  value: string;
  label: string;
  description: string;
  /** When true, hidden from the Labs experience picker (Architectural-only division). */
  hiddenFromLabs?: boolean;
  /** Optional suffix rendered on the homepage list (e.g. Performance handoff). */
  homepageSuffix?: string;
}

export const PROJECT_TYPES: ProjectTypeOption[] = [
  {
    value: "Hospitality",
    label: "Hospitality",
    description:
      "Scenes, dimming curves, warmth, and glare tuned for hospitality and guest-experience projects.",
  },
  {
    value: "Workplace",
    label: "Workplace",
    description:
      "Optics, glare control, tunable-white, and controls tuned for focus and collaboration projects.",
  },
  {
    value: "Multifamily",
    label: "Multifamily",
    description:
      "Amenity, corridor, and unit lighting with the controls layer to keep spec intent through turnover.",
  },
  {
    value: "High-end Residential",
    label: "High-end Residential",
    description:
      "Layered light, CCT, and control scenes for single-family and estate-level residential projects.",
  },
  {
    value: "Retail",
    label: "Retail",
    description:
      "Color, contrast, and vertical illuminance calibrated to product, with a quiet ceiling and operable scenes.",
  },
  {
    value: "Civic / Institutional",
    label: "Civic / Institutional",
    description:
      "Durability, safety, identity, and public experience for civic and institutional projects.",
  },
  {
    value: "Cultural",
    label: "Cultural",
    description:
      "Precision light for art, architecture, storytelling, and preservation in cultural projects.",
  },
  {
    value: "Commercial",
    label: "Commercial",
    description:
      "Brand expression, customer experience, and long-term performance for commercial projects.",
  },
  {
    value: "Other",
    label: "Other",
    description: "Something that doesn't fit the categories above.",
  },
];

/** Values used by form <Select> dropdowns (Design + Project Review). */
export const FORM_PROJECT_TYPES = PROJECT_TYPES.map((p) => p.value);

/** Options for the Labs experience picker. */
export const LABS_EXPERIENCE_OPTIONS = PROJECT_TYPES.filter(
  (p) => !p.hiddenFromLabs && p.value !== "Other",
).map(({ value, description }) => ({ value, desc: description }));
